"use client";
import * as stylex from "@stylexjs/stylex";
import { type RefObject, useEffect, useSyncExternalStore } from "react";
import { controlStyles as styles } from "./replay-control.styles";

// Safari still ships the prefixed Fullscreen API, so probe both spellings.
type FullscreenElement = HTMLDivElement & {
  webkitRequestFullscreen?: () => void;
};
type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
  webkitExitFullscreen?: () => void;
};

function currentFullscreenElement(): Element | null {
  const doc: FullscreenDocument = document;
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

function fullscreenSupported(): boolean {
  const doc: FullscreenDocument = document;
  return Boolean(document.fullscreenEnabled ?? doc.webkitFullscreenEnabled);
}

function subscribeFullscreen(onChange: () => void) {
  document.addEventListener("fullscreenchange", onChange);
  document.addEventListener("webkitfullscreenchange", onChange);
  return () => {
    document.removeEventListener("fullscreenchange", onChange);
    document.removeEventListener("webkitfullscreenchange", onChange);
  };
}

const serverFullscreen = () => false;

type EmbedFullscreenButtonProps = Readonly<{
  // Expand the same-origin container so the cross-origin iframe can fill it.
  targetRef: RefObject<HTMLDivElement | null>;
  label?: string;
}>;

export function EmbedFullscreenButton({
  targetRef,
  label = "replay",
}: EmbedFullscreenButtonProps) {
  const supported = useSyncExternalStore(
    subscribeFullscreen,
    fullscreenSupported,
    serverFullscreen,
  );
  const isFullscreen = useSyncExternalStore(
    subscribeFullscreen,
    () =>
      targetRef.current !== null &&
      currentFullscreenElement() === targetRef.current,
    serverFullscreen,
  );

  useEffect(() => {
    const target = targetRef.current;

    if (!isFullscreen || target === null) return;

    const height = target.style.getPropertyValue("height");
    const heightPriority = target.style.getPropertyPriority("height");
    const minHeight = target.style.getPropertyValue("min-height");
    const minHeightPriority = target.style.getPropertyPriority("min-height");
    target.style.setProperty("height", "100vh", "important");
    target.style.setProperty("min-height", "0", "important");
    return () => {
      target.style.setProperty("height", height, heightPriority);
      target.style.setProperty("min-height", minHeight, minHeightPriority);
    };
  }, [isFullscreen, targetRef]);

  if (!supported) {
    return null;
  }

  const toggle = () => {
    const target: FullscreenElement | null = targetRef.current;

    if (!target) {
      return;
    }

    if (currentFullscreenElement() === target) {
      const doc: FullscreenDocument = document;
      (document.exitFullscreen ?? doc.webkitExitFullscreen)?.call(document);
    } else {
      (target.requestFullscreen ?? target.webkitRequestFullscreen)?.call(
        target,
      );
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={
        isFullscreen ? "Exit full screen" : `View ${label} full screen`
      }
      title={isFullscreen ? "Exit full screen" : "Full screen"}
      {...stylex.props(styles.control, styles.fullscreen)}
    >
      <FullscreenGlyph active={isFullscreen} />
    </button>
  );
}

function FullscreenGlyph({ active }: Readonly<{ active: boolean }>) {
  const paths = active
    ? [
        "M8 3v3a2 2 0 0 1-2 2H3",
        "M21 8h-3a2 2 0 0 1-2-2V3",
        "M3 16h3a2 2 0 0 1 2 2v3",
        "M16 21v-3a2 2 0 0 1 2-2h3",
      ]
    : [
        "M8 3H5a2 2 0 0 0-2 2v3",
        "M16 3h3a2 2 0 0 1 2 2v3",
        "M8 21H5a2 2 0 0 1-2-2v-3",
        "M16 21h3a2 2 0 0 0 2-2v-3",
      ];
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths.map((path) => (
        <path key={path} d={path} />
      ))}
    </svg>
  );
}
